import { AppUserSettingService } from '../../services/user-services/app-user-setting/app-user-setting.service';
import { DEFAULT_BUSINESS_SOLUTIONS } from '@aca-new/app/pages/home/shared/constants/default-business-solutions.constants';
import { EBusinessSolution } from '@aca-new/app/shared/models/enums/business-solution.enum';
import { ELocalForageKeys } from '@aca-new/app/shared/models/enums/localforage-keys.enum';
import { ISelect } from '@aca-new/app/shared/models/interfaces/select.interface';
import { UserIsolatedLocalForageService } from '@aca-new/app/shared/services/storage-services/user-isolated-localforage.service';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { EQimaInputSelectStyle } from '@qima/ngx-qima';
import { isEmpty } from 'lodash/index';

const FOOD_CERT_BUSINESS_SOLUTIONS: readonly EBusinessSolution[] = [EBusinessSolution.ALL, EBusinessSolution.FOOD_CERTIFICATION, EBusinessSolution.AUDITS];

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'app-business-solution-select',
  styleUrls: ['./business-solution-select.component.scss'],
  templateUrl: './business-solution-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BusinessSolutionSelectComponent implements OnInit, OnChanges {
  /**
   * @description
   * Default selected business solution
   * @type {string}
   * @default EBusinessSolution.INSPECTIONS
   */
  @Input('businessSolutionSelected')
  public selected: string = EBusinessSolution.INSPECTIONS;

  /**
   * @description
   * The business solution options
   * @type {ISelect[]}
   * @default []
   */
  @Input('businessSolutions')
  public businessSolutions: ISelect[] = [];

  /**
   * @description
   * max height of the dropdown menu
   * @type {number}
   * @default 400
   */
  @Input('businessSolutionMenuMaxHeight')
  public businessSolutionMenuMaxHeight: number = 400;

  @Output('businessSolutionChange')
  public readonly businessSolutionChange: EventEmitter<Readonly<EBusinessSolution>> = new EventEmitter<Readonly<EBusinessSolution>>();

  public selectedStyle: EQimaInputSelectStyle = EQimaInputSelectStyle.PRIMARY;
  private _isInitializedFromLocalforage = false;

  public constructor(
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _appUserSettingService: AppUserSettingService,
    private readonly _userIsolatedLocalForageService: UserIsolatedLocalForageService
  ) {}

  public ngOnInit(): void {
    this._initializeFromLocalforage();
    this._processBusinessSolutions();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    // Only update from localforage if this is the first time and no parent input is provided
    if (changes['selected'] && !this._isInitializedFromLocalforage && !changes['selected'].firstChange) {
      this._isInitializedFromLocalforage = true;
    }
  }

  public onBusinessSolutionChange(selected: Readonly<string>): void {
    void this._userIsolatedLocalForageService.setItem(ELocalForageKeys.SELECTED_BUSINESS_SOLUTION, selected);
    this.businessSolutionChange.emit(selected as EBusinessSolution);
  }

  public updateView(): void {
    this._changeDetectorRef.markForCheck();
  }

  private _initializeFromLocalforage(): void {
    // Only initialize from localforage if no parent input is provided
    if (!this._isInitializedFromLocalforage) {
      void this._userIsolatedLocalForageService.getItem<EBusinessSolution>(ELocalForageKeys.SELECTED_BUSINESS_SOLUTION).then((storedSolution): void => {
        if (!this._isInitializedFromLocalforage) {
          this.selected = storedSolution || this.businessSolutions[0]?.value || EBusinessSolution.INSPECTIONS;
          this._isInitializedFromLocalforage = true;
          this._changeDetectorRef.markForCheck();
        }
      });
    }
  }

  private _processBusinessSolutions(): void {
    if (this._appUserSettingService.getEnableFoodCertification()) {
      this.businessSolutions = this.businessSolutions.filter((item): boolean => FOOD_CERT_BUSINESS_SOLUTIONS.includes(item.value as EBusinessSolution));

      if (!this.businessSolutions.map((it): string => it.value).includes(this.selected)) {
        this.selected = EBusinessSolution.AUDITS;
      }
    }

    // TODO: The business solutions should be retrieved from parent component, remove below codes once it's done
    if (isEmpty(this.businessSolutions)) {
      this.businessSolutions = DEFAULT_BUSINESS_SOLUTIONS;
    }
  }
}
